/**
 * Voice & Smart Intake screen — WO-24.
 *
 * FLOW (live, edge STT — Gate Q5 RESOLVED via expo-speech-recognition)
 * ────
 * 1. User taps "Start Recording" → activeTranscriber.transcribeLive() requests
 *    mic + speech-recognition permission and starts a native on-device session.
 * 2. User speaks; taps "Stop" → session.stop() asks the recognizer to finalise.
 * 3. The session resolves with the final transcript, which prefills the editable
 *    TextInput. The user can still edit before sending.
 *    - Permission denied / unsupported / recognizer error: a typed SttError is
 *      caught and the screen falls through to the editable manual TextInput.
 *      web-speech-api is NEVER used.
 * 4. User selects intent (Capture/Ingest, Ask Assistant, Draft Estimate).
 * 5. User taps "Send" → routeVoice(transcript, intent) → POST /api/sb/voice.
 *    Only the TEXT transcript leaves the device — never audio.
 * 6. Response routing:
 *      ingest    → addBatch to queueStore → navigate to /review.
 *      assistant → show assistantReply inline.
 *      estimator → navigate to /estimator with draftId param (or show it).
 *
 * AUTHORITY (CLAUDE.md invariant 6, master spec §4)
 * ─────────────────────────────────────────────────
 * Staff may reach this screen and voice-draft estimates. No "Approve",
 * "Send for Signature", or "Lock" actions exist here. Those are owner-only
 * steps in the BEAM estimate lifecycle enforced server-side (WO-16).
 *
 * STT NOTE
 * ────────
 * On-device transcription is performed by expo-speech-recognition (native
 * SFSpeechRecognizer / android.speech.SpeechRecognizer). web-speech-api is
 * NEVER used (unavailable in Expo; mobile CLAUDE.md invariant 5). The manual
 * transcript TextInput remains the fallback when permission is denied or
 * recognition fails — fully functional and testable.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 *
 * WO-M0 — design-system refactor. All styling via theme tokens + UI primitives.
 */

import { useState, useRef } from 'react';
import {
  View,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import type { VoiceIntent } from '@beam/api-types';
import {
  activeTranscriber,
  manualTranscript,
  SttError,
  type LiveTranscription,
} from '../../src/voice/transcribe';
import { routeVoice } from '../../src/api/voice';
import { useQueueStore } from '../../src/review/queueStore';
import { ApiRequestError } from '../../src/api/client';
import type { BatchResult } from '@beam/api-types';
import { BatchResult as BatchResultSchema } from '@beam/api-types';
import { useAssistantAction } from '../../src/components/assistant/useAssistantAction';
import { routeForTarget } from '../../src/lib/assistantRoutes';
import { updateTask } from '../../src/api/tasks';
import type { AssistantActionResult } from '../../src/api/assistant';
import { Screen, Text, Button, Card, Field } from '../../src/components/ui';
import { theme } from '../../src/theme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VoiceMode =
  | 'idle'        // nothing has happened yet
  | 'recording'   // mic is active
  | 'transcribing' // recognizer is finalising after Stop
  | 'editing'     // user is editing transcript + choosing intent
  | 'routing'     // POST /api/sb/voice or /api/assistant/act in flight
  | 'done_action' // the action router returned a result (action/clarification/needs_owner/answer)
  | 'done_assistant' // assistant reply is ready
  | 'done_estimator' // estimator draft is ready (draftId shown)
  | 'error';      // any recoverable error

/** The intake intents. 'action' is the hero — the router classifies + EXECUTES the spoken request. */
type IntakeIntent = VoiceIntent | 'action';

interface IntentOption {
  value: IntakeIntent;
  label: string;
  description: string;
}

const INTENT_OPTIONS: IntentOption[] = [
  {
    value: 'action',
    label: 'Do it',
    description: 'Just say what you need — assign a task, schedule a visit, log a receipt, start an estimate.',
  },
  {
    value: 'ingest',
    label: 'Capture / Ingest',
    description: 'Extract facts from the note and send to the review queue.',
  },
  {
    value: 'assistant',
    label: 'Ask Assistant',
    description: 'Ask the BEAM assistant a question in context.',
  },
  {
    value: 'estimator',
    label: 'Draft Estimate',
    description:
      'Voice-draft an estimate (staff may present; owner approves server-side).',
  },
];

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function VoiceScreen() {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { submit } = useAssistantAction();
  const addBatch = useQueueStore((s) => s.addBatch);

  const [mode, setMode] = useState<VoiceMode>('idle');
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  const [sttProviderNote, setSttProviderNote] = useState<string | null>(null);
  const [selectedIntent, setSelectedIntent] = useState<IntakeIntent>('action');
  const [assistantReply, setAssistantReply] = useState<string | null>(null);
  const [estimatorDraftId, setEstimatorDraftId] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<AssistantActionResult | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);

  // Live recognition session handle (expo-speech-recognition)
  const sessionRef = useRef<LiveTranscription | null>(null);

  // ---------------------------------------------------------------------------
  // Live on-device recognition (start → speak → stop → final transcript)
  // ---------------------------------------------------------------------------

  async function handleStartRecording() {
    setError(null);
    setSttProviderNote(null);

    let session: LiveTranscription;
    try {
      // Requests mic + speech-recognition permission and starts the native
      // on-device session. Throws a typed SttError on denial/unsupported.
      session = await activeTranscriber.transcribeLive();
    } catch (sttErr) {
      handleSttFailure(sttErr);
      setMode('editing');
      return;
    }

    sessionRef.current = session;
    setMode('recording');

    // Await the final transcript in the background. Resolves when the user taps
    // Stop (or the recognizer auto-finalises); rejects with a typed SttError.
    session.result
      .then((text) => {
        if (sessionRef.current !== session) return; // a newer session/reset won
        sessionRef.current = null;
        setTranscript(text);
        setSttProviderNote(null);
        setMode('editing');
      })
      .catch((sttErr: unknown) => {
        if (sessionRef.current !== session) return;
        sessionRef.current = null;
        // 'aborted' means the user discarded — stay quiet, return to idle.
        if (sttErr instanceof SttError && sttErr.code === 'stt_aborted') {
          setMode('idle');
          return;
        }
        handleSttFailure(sttErr);
        setMode('editing');
      });
  }

  function handleStopRecording() {
    const session = sessionRef.current;
    if (!session) return;
    setMode('transcribing');
    // Ask the recognizer to finalise; session.result resolves via the listener.
    session.stop();
  }

  /**
   * Translate an STT failure into the manual-entry fallback note. NEVER falls
   * back to web-speech-api — the user types their transcript instead.
   */
  function handleSttFailure(sttErr: unknown) {
    if (sttErr instanceof SttError) {
      switch (sttErr.code) {
        case 'stt_permission_denied':
          setSttProviderNote(
            'Speech recognition permission is off. ' +
              'Type your transcript below, or enable it in Settings and try again.',
          );
          break;
        case 'stt_unsupported':
          setSttProviderNote(
            'On-device speech recognition is not available on this device. ' +
              'Type your transcript below.',
          );
          break;
        case 'stt_no_speech_detected':
          setSttProviderNote('No speech was detected. Type your transcript below or try again.');
          break;
        default:
          setSttProviderNote(
            `Transcription failed (${sttErr.code}). Type your transcript below.`,
          );
      }
    } else {
      setSttProviderNote('Transcription failed unexpectedly. Type your transcript below.');
    }
    setTranscript('');
  }

  // ---------------------------------------------------------------------------
  // Send to backend
  // ---------------------------------------------------------------------------

  async function handleSend() {
    const text = manualTranscript(transcript);
    if (!text) {
      setError('Please enter a transcript before sending.');
      return;
    }

    setError(null);
    setMode('routing');

    // Hero path — "Do it": send the free text to the universal action router (the same client the
    // Assistant dock uses). The router classifies + EXECUTES (create/assign task, schedule, log receipt,
    // draft estimate) and returns an envelope we render. navigate=false so we show a confirm/undo card
    // instead of jumping straight to the entity.
    if (selectedIntent === 'action') {
      const result = await submit(text, { route: pathname ?? '' }, false);
      if (!result) {
        setError('Could not reach the assistant. Please try again.');
        setMode('editing');
        return;
      }
      if (result.kind === 'action') {
        // The created/updated entity should show up immediately in Tasks + the unified work queue.
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
        void queryClient.invalidateQueries({ queryKey: ['work-queue'] });
      }
      setUndone(false);
      setActionResult(result);
      setMode('done_action');
      return;
    }

    try {
      const response = await routeVoice(text, selectedIntent as VoiceIntent);

      switch (response.intent) {
        case 'ingest': {
          // The backend ingested the transcript and returned a batchId.
          // Build a minimal BatchResult and push it into the review queue.
          if (response.batchId) {
            // Construct a BatchResult from the voice capture response.
            // The backend's voice route creates a SmartBox batch via ingest()
            // and returns the batchId. We surface it as a pending batch.
            const batch: BatchResult = BatchResultSchema.parse({
              batchId: response.batchId,
              fileResults: [],
              pendingConfirmation: true,
            });
            addBatch(batch);
          }
          router.replace('/(app)/review');
          break;
        }

        case 'assistant': {
          setAssistantReply(response.assistantReply ?? '(no reply)');
          setMode('done_assistant');
          break;
        }

        case 'estimator': {
          setEstimatorDraftId(response.draftId ?? null);
          setMode('done_estimator');
          break;
        }

        default: {
          // Exhaustive guard — api-types VoiceIntent enum is 'ingest'|'assistant'|'estimator'
          setMode('editing');
        }
      }
    } catch (err) {
      setError(formatError(err, 'Failed to route voice note.'));
      setMode('editing');
    }
  }

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------

  function handleReset() {
    // Abort any in-flight recognition session so the mic is released.
    if (sessionRef.current) {
      sessionRef.current.abort();
      sessionRef.current = null;
    }
    setMode('idle');
    setError(null);
    setTranscript('');
    setSttProviderNote(null);
    setAssistantReply(null);
    setEstimatorDraftId(null);
    setActionResult(null);
    setUndoing(false);
    setUndone(false);
  }

  /** Return to the editor with the transcript preserved (for a clarification, or to correct + resend). */
  function handleEditAgain() {
    setActionResult(null);
    setError(null);
    setMode('editing');
  }

  /**
   * Undo a just-executed action. For a created/assigned task we cancel it via the existing
   * PATCH /api/tasks/:id { status:'cancelled' } (a real reversal — no new endpoint). For any other
   * target we return to the editor so the user can correct and resend.
   */
  async function handleUndo() {
    const target = actionResult?.target;
    if (target?.type === 'task') {
      setUndoing(true);
      setError(null);
      try {
        await updateTask(target.id, { status: 'cancelled' });
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
        void queryClient.invalidateQueries({ queryKey: ['work-queue'] });
        setUndone(true);
      } catch (err) {
        setError(formatError(err, 'Could not undo that. Open the task to change it.'));
      } finally {
        setUndoing(false);
      }
      return;
    }
    handleEditAgain();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function formatError(err: unknown, fallback: string): string {
    if (err instanceof ApiRequestError) return err.message;
    if (err instanceof Error) return err.message;
    return fallback;
  }

  // ---------------------------------------------------------------------------
  // Recording UI (overlays the rest)
  // ---------------------------------------------------------------------------

  if (mode === 'recording') {
    return (
      <Screen>
        <View style={styles.centered}>
          {/* Pulse indicator — uses overdue bg/fg for the danger/recording tone */}
          <View style={styles.recordingPulse} accessibilityElementsHidden />
          <Text variant="title" accessibilityRole="text">
            Listening…
          </Text>
          <Text variant="body" color="secondary" accessibilityLiveRegion="polite">
            Transcribing on this device. Speak clearly into the mic.
          </Text>

          <Pressable
            style={({ pressed }) => [styles.stopButton, pressed && styles.stopButtonPressed]}
            onPress={handleStopRecording}
            accessibilityRole="button"
            accessibilityLabel="Stop recording"
          >
            <View style={styles.stopIcon} />
          </Pressable>
          <Text variant="small" color="secondary">Tap to stop</Text>
        </View>
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Transcribing spinner
  // ---------------------------------------------------------------------------

  if (mode === 'transcribing') {
    return (
      <Screen>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.color.action.primary} />
          <Text variant="subhead">Processing audio…</Text>
          <Text variant="body" color="secondary">
            Attempting on-device transcription.
          </Text>
        </View>
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Routing spinner
  // ---------------------------------------------------------------------------

  if (mode === 'routing') {
    return (
      <Screen>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.color.action.primary} />
          <Text variant="subhead">Sending to BEAM…</Text>
          <Text variant="body" color="secondary">Routing your voice note.</Text>
        </View>
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Done — action router result (execute / clarify / owner-gated / answer)
  // ---------------------------------------------------------------------------

  if (mode === 'done_action' && actionResult) {
    const r = actionResult;
    return (
      <Screen scroll padded>
        {r.kind === 'action' ? (
          <>
            <Text variant="title">Done</Text>
            <Card style={styles.noteCard}>
              <Text variant="bodyStrong" accessibilityRole="text">{`✓ ${r.summary}`}</Text>
              {undone ? (
                <Text variant="small" color="secondary" accessibilityRole="alert">Undone — the task was cancelled.</Text>
              ) : null}
            </Card>
            {r.target && !undone ? (
              <Button
                title={`Open ${r.target.type.replace('_', ' ')}`}
                variant="primary"
                onPress={() => router.push(routeForTarget(r.target!))}
                accessibilityLabel={`Open ${r.target.type}`}
              />
            ) : null}
            {!undone ? (
              // A task can be truly undone (cancelled server-side). Other targets have no reversal
              // endpoint, so offer an honest "Edit / redo" instead of a misleading "Undo".
              r.target?.type === 'task' ? (
                <Button title="Undo" variant="secondary" onPress={handleUndo} loading={undoing} disabled={undoing} accessibilityLabel="Undo" />
              ) : (
                <Button title="Edit / redo" variant="secondary" onPress={handleEditAgain} accessibilityLabel="Edit or redo" />
              )
            ) : null}
            {error ? (
              <Text variant="body" color="danger" accessibilityRole="alert">{error}</Text>
            ) : null}
            <Button title="Record another" variant="ghost" onPress={handleReset} accessibilityLabel="Record another voice note" />
          </>
        ) : r.kind === 'clarification' ? (
          <>
            <Text variant="title">One quick question</Text>
            <Card style={styles.infoCard}>
              <Text variant="body" accessibilityRole="text">{r.clarification ?? r.summary}</Text>
            </Card>
            <Button title="Edit my request" variant="primary" onPress={handleEditAgain} accessibilityLabel="Edit my request" />
            <Button title="Start over" variant="secondary" onPress={handleReset} accessibilityLabel="Start over" />
          </>
        ) : r.kind === 'needs_owner' ? (
          <>
            <Text variant="title">Needs owner approval</Text>
            <Card style={styles.authorityCard}>
              <Text variant="label" color="secondary">NEEDS OWNER APPROVAL</Text>
              <Text variant="body" accessibilityRole="text">{r.reason ?? r.summary}</Text>
              <Text variant="small" color="secondary">
                An owner has to approve this before it can be applied. Nothing was changed here.
              </Text>
            </Card>
            <Button title="Record another" variant="primary" onPress={handleReset} accessibilityLabel="Record another voice note" />
          </>
        ) : (
          <>
            <Text variant="title">Assistant</Text>
            <Card>
              <Text variant="body" accessibilityRole="text">{r.content ?? r.summary}</Text>
            </Card>
            <Button title="Record another" variant="primary" onPress={handleReset} accessibilityLabel="Record another voice note" />
          </>
        )}
        <Button title="Back to Home" variant="secondary" onPress={() => router.back()} accessibilityLabel="Go back to home" />
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Done — assistant reply
  // ---------------------------------------------------------------------------

  if (mode === 'done_assistant') {
    return (
      <Screen scroll padded>
        <Text variant="title">Assistant Reply</Text>
        <Card>
          <Text variant="body" accessibilityRole="text">{assistantReply}</Text>
        </Card>
        <Button
          title="Record Another"
          variant="primary"
          onPress={handleReset}
          accessibilityLabel="Record another voice note"
        />
        <Button
          title="Back to Home"
          variant="secondary"
          onPress={() => router.back()}
          accessibilityLabel="Go back to home"
        />
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Done — estimator draft
  // ---------------------------------------------------------------------------

  if (mode === 'done_estimator') {
    return (
      <Screen scroll padded>
        <Text variant="title">Estimate Draft Ready</Text>
        <Card style={styles.noteCard}>
          <Text variant="small" color="secondary">
            Your voice note has been used to advance an estimate draft.
            Staff may present a full estimate — owner approval is required
            server-side before sending for signature.
          </Text>
        </Card>
        {estimatorDraftId ? (
          <Card>
            <Text variant="label" color="secondary">Draft ID</Text>
            <Text variant="data" selectable>{estimatorDraftId}</Text>
          </Card>
        ) : null}
        <Button
          title="Open Estimator"
          variant="primary"
          onPress={() => router.replace('/(app)/estimator')}
          accessibilityLabel="Open the estimator"
        />
        <Button
          title="Record Another"
          variant="secondary"
          onPress={handleReset}
          accessibilityLabel="Record another voice note"
        />
        <Button
          title="Back to Home"
          variant="secondary"
          onPress={() => router.back()}
          accessibilityLabel="Go back to home"
        />
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Main idle + editing + error view
  // ---------------------------------------------------------------------------

  const isEditing = mode === 'editing';

  return (
    <Screen scroll padded>
      <Text variant="title">Voice Note</Text>
      <Text variant="body" color="secondary">
        Record a voice note and route it to ingest, the assistant, or an
        estimate draft. Transcription runs on this device — you can edit the
        text before sending, or type it directly.
      </Text>

      {/* Error banner */}
      {error ? (
        <Card style={styles.errorCard}>
          <Text variant="body" color="danger" accessibilityRole="alert">{error}</Text>
          <Button
            title="Dismiss"
            variant="danger"
            size="sm"
            fullWidth={false}
            onPress={() => {
              setError(null);
              if (mode === 'error') setMode('idle');
            }}
            accessibilityLabel="Dismiss error"
          />
        </Card>
      ) : null}

      {/* STT provider note (manual-fallback informational) */}
      {sttProviderNote ? (
        <Card style={styles.infoCard}>
          <Text variant="small" color="secondary" accessibilityRole="text">{sttProviderNote}</Text>
        </Card>
      ) : null}

      {/* Record button (idle state) */}
      {!isEditing ? (
        <View style={styles.recordSection}>
          <Pressable
            style={({ pressed }) => [
              styles.recordButton,
              pressed && styles.recordButtonPressed,
            ]}
            onPress={handleStartRecording}
            accessibilityRole="button"
            accessibilityLabel="Start recording voice note"
          >
            <View style={styles.micIcon} accessibilityElementsHidden />
            <Text variant="bodyStrong" color="onPrimary">Start Recording</Text>
          </Pressable>
        </View>
      ) : null}

      {/* Transcript editor — always shown when in editing mode;
          also shown in idle mode (manual-entry even without a recording).
          The field is always editable so the flow works without STT. */}
      <View style={styles.transcriptSection}>
        <Field
          label={`Transcript${!isEditing ? ' (optional: type without recording)' : ''}`}
          value={transcript}
          onChangeText={(t) => {
            setTranscript(t);
            // If the user starts typing from idle, move to editing mode
            if (mode === 'idle' && t.length > 0) setMode('editing');
          }}
          multiline
          numberOfLines={Platform.OS === 'ios' ? undefined : 5}
          placeholder="Type or review your transcript here…"
          accessibilityLabel="Transcript text field"
          accessibilityHint="Edit or enter your voice note transcript"
          textAlignVertical="top"
          style={styles.transcriptInputStyle}
        />
        {isEditing ? (
          <Button
            title="Discard and record again"
            variant="ghost"
            size="sm"
            fullWidth={false}
            onPress={handleReset}
            accessibilityLabel="Discard and record again"
            style={styles.rerecordLink}
          />
        ) : null}
      </View>

      {/* Intent selector */}
      <View style={styles.intentSection}>
        <Text variant="label" color="secondary">Route to</Text>
        <View style={styles.intentOptions} accessibilityRole="radiogroup">
          {INTENT_OPTIONS.map((opt) => {
            const selected = selectedIntent === opt.value;
            return (
              <Pressable
                key={opt.value}
                style={({ pressed }) => [
                  styles.intentOption,
                  selected && styles.intentOptionSelected,
                  pressed && !selected && styles.intentOptionPressed,
                ]}
                onPress={() => setSelectedIntent(opt.value)}
                accessibilityRole="radio"
                accessibilityState={{ checked: selected }}
                accessibilityLabel={opt.label}
                accessibilityHint={opt.description}
              >
                <View style={styles.intentOptionHeader}>
                  <View
                    style={[
                      styles.radioCircle,
                      selected && styles.radioCircleSelected,
                    ]}
                  >
                    {selected ? <View style={styles.radioDot} /> : null}
                  </View>
                  <Text variant="bodyStrong" color={selected ? 'link' : 'primary'}>
                    {opt.label}
                  </Text>
                </View>
                <Text variant="small" color="secondary" style={styles.intentDescription}>
                  {opt.description}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Staff authority note */}
      <Card style={styles.authorityCard}>
        <Text variant="small" color="secondary" accessibilityRole="text">
          Staff may voice-draft and present estimates. Binding approval and
          send-for-signature require owner — enforced server-side.
        </Text>
      </Card>

      {/* Send button */}
      <Button
        title="Send"
        variant="primary"
        size="lg"
        onPress={handleSend}
        disabled={!transcript.trim()}
        accessibilityLabel="Send voice note"
      />

      <Button
        title="Back to Home"
        variant="secondary"
        onPress={() => router.back()}
        accessibilityLabel="Go back to home"
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  // Error / info cards
  errorCard: {
    backgroundColor: theme.status.overdue.bg,
    borderColor: theme.status.overdue.bg,
  },
  infoCard: {
    backgroundColor: theme.status.inProgress.bg,
    borderColor: theme.status.inProgress.bg,
  },
  noteCard: {
    backgroundColor: theme.status.paid.bg,
    borderColor: theme.status.paid.bg,
  },
  authorityCard: {
    backgroundColor: theme.status.sent.bg,
    borderColor: theme.status.sent.bg,
  },
  // Recording circle — danger (red) tone via status tokens
  recordSection: {
    alignItems: 'center',
  },
  recordButton: {
    width: 160,
    height: 160,
    borderRadius: theme.radius.xl,
    backgroundColor: theme.color.action.primary,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.sm,
    ...theme.shadow.card,
  },
  recordButtonPressed: {
    backgroundColor: theme.palette.blueprint,
  },
  micIcon: {
    width: theme.spacing['3xl'] + theme.spacing.xs,
    height: theme.layout.minTapTarget,
    borderRadius: theme.radius.lg,
    backgroundColor: theme.palette.white,
    opacity: 0.9,
  },
  // Active recording pulse — overdue (danger) tone
  recordingPulse: {
    width: theme.spacing['4xl'] * 2,
    height: theme.spacing['4xl'] * 2,
    borderRadius: theme.spacing['4xl'],
    backgroundColor: theme.status.overdue.bg,
    borderWidth: theme.spacing.xs,
    borderColor: theme.color.action.danger,
  },
  stopButton: {
    width: 72,
    height: 72,
    borderRadius: theme.radius.xl,
    backgroundColor: theme.color.action.danger,
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadow.card,
  },
  stopButtonPressed: {
    opacity: 0.8,
  },
  stopIcon: {
    width: theme.spacing['3xl'] - theme.spacing.xs,
    height: theme.spacing['3xl'] - theme.spacing.xs,
    borderRadius: theme.radius.sm / 2,
    backgroundColor: theme.palette.white,
  },
  // Transcript
  transcriptSection: {
    gap: theme.spacing.sm,
  },
  transcriptInputStyle: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  rerecordLink: {
    alignSelf: 'flex-end',
  },
  // Intent selector
  intentSection: {
    gap: theme.spacing.sm,
  },
  intentOptions: {
    gap: theme.spacing.sm,
  },
  intentOption: {
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    gap: theme.spacing.xs,
  },
  intentOptionSelected: {
    borderColor: theme.color.action.primary,
    backgroundColor: theme.color.focusRing,
  },
  intentOptionPressed: {
    backgroundColor: theme.color.bg.subtle,
  },
  intentOptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  radioCircle: {
    width: theme.spacing.xl,
    height: theme.spacing.xl,
    borderRadius: theme.spacing.sm + theme.spacing.xs,
    borderWidth: theme.layout.hairline * 2,
    borderColor: theme.color.border.strong,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioCircleSelected: {
    borderColor: theme.color.action.primary,
  },
  radioDot: {
    width: theme.spacing.sm + theme.spacing.xs,
    height: theme.spacing.sm + theme.spacing.xs,
    borderRadius: theme.spacing.sm,
    backgroundColor: theme.color.action.primary,
  },
  intentDescription: {
    marginLeft: theme.spacing['3xl'] - theme.spacing.xs,
  },
});
